import * as React from 'react';
import Styled, { css } from 'styled-components';
import { v4 } from 'uuid';

import { media } from '../../lib/StyledComponents';
import { ActionButton } from '../ActionButton';
import { Card } from '../Card';
import { ConditionalRenderWrapper } from '../Mobile';
import { SVGIcon } from '../Icon';

interface IProps {
  id?: string;
  maxHeight?: string;
  minWidth?: string;
  visible: boolean;
}

interface IDropdownProps extends Omit<IProps, 'visible'> {
  buttonContent: React.ReactNode;
  alignRight?: boolean;
  forceVisible?: boolean;
  active?: boolean;
}

interface IDropdownState {
  dropdownVisible: boolean;
}

const DropdownContain = Styled.div<Pick<IDropdownProps, 'alignRight'>>`
  display: flex;
  justify-content: ${({ alignRight }) => !!alignRight ? 'flex-end' : 'flex-start'};

  ${media.small`
    position: relative;
  `}
`;

const notVisibleStyles = css`
  display: none;
  padding: 0;
  border: none;
  max-height: 0;
  box-shadow: none;
`;

const DEFAULT_WIDTH: string = '20em';

const DropdownCard = Styled(Card)<IProps>`
  position: fixed;
  max-height: none;
  overflow: scroll;
  width: 100%;
  height: 100%;
  padding: 3.5em 2.25em 1.5em;
  left: 0;
  top: 0;
  z-index: 9999;

  ${({ visible }) => !visible && notVisibleStyles}

  ${({ visible, minWidth, maxHeight }) => css`
    ${media.small`
      position: absolute;
      padding: 2em 2.625em;
      top: 3.25em;
      left: initial;
      width: auto;
      height: auto;
      z-index: 2;
      min-width: ${minWidth || `${DEFAULT_WIDTH}`};
      max-height: ${maxHeight || 'none'};

      ${!visible && notVisibleStyles}
    `}
  `}
`;

const CloseButton = Styled.button`
  position: absolute;
  top: 1.125em;
  right: 1.125em;
  width: 12px;
  height: 12px;

  &:hover {
    svg {
      color: ${({ theme: { link } }) => link.colorLinkHover};
      fill: ${({ theme: { link } }) => link.colorLinkHover};
    }
  }
`;

export class DropdownButton extends React.Component<IDropdownProps, IDropdownState> {
  public state = {
    dropdownVisible: false,
  };

  private readonly dropdownContainRef = React.createRef<HTMLDivElement>();
  private readonly _id: string = v4();

  public componentDidMount () {
    document.addEventListener('mousedown', this.handleClick, false);
  }

  public componentWillUnmount () {
    document.removeEventListener('mousedown', this.handleClick, false);
  }

  public render (): JSX.Element {
    const {
      active,
      alignRight,
      buttonContent,
      children,
      forceVisible,
      maxHeight,
      minWidth,
      ...rest
    } = this.props;

    return (
      <DropdownContain {...rest} alignRight={alignRight} ref={this.dropdownContainRef}>
        <ActionButton
          onClick={this.toggleDropdown}
          active={this.state.dropdownVisible || !!forceVisible || active}
          aria-controls={this._id}
          aria-expanded={this.state.dropdownVisible || !!forceVisible}
        >
          {buttonContent}
        </ActionButton>
        <DropdownCard
          id={this._id}
          visible={this.state.dropdownVisible || !!forceVisible}
          minWidth={minWidth}
          maxHeight={maxHeight}
        >
          <ConditionalRenderWrapper
            onClick={this.toggleDropdown}
            breakpoint='small'
            hiddenOnMobile={false}
          >
            <CloseButton>
              <SVGIcon icon='close' />
            </CloseButton>
          </ConditionalRenderWrapper>
          {children}
        </DropdownCard>
      </DropdownContain>
    );
  }

  private readonly toggleDropdown: React.MouseEventHandler = () => {
    this.setState(({ dropdownVisible }) => ({ dropdownVisible: !dropdownVisible}));
  }

  private readonly handleClick: (e: MouseEvent) => void = e => {
    if (!this.state.dropdownVisible) {
      return;
    }

    const dropdownContainContent = this.dropdownContainRef.current;
    const targetElement: Element = e.target as Element;

    if (
      !dropdownContainContent ||
      targetElement === dropdownContainContent ||
      dropdownContainContent.contains(targetElement)
    ) {
      return;
    }

    this.setState({ dropdownVisible: false });
  }
}
